import { Archive, BookOpen, Database, Edit3, Eye, Grid3x3, Layers, List } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { cn } from '@/lib/utils/cn';
import {
  useTemplateVersionsQuery,
  type LibraryKind,
  type TemplateSummary,
  type TemplateVersionSummary,
} from '@/lib/api/methodology-library';
import { formatDate } from '@/lib/utils/format';

/**
 * FrameworkCard — one row in the methodology library grid (T-0111 /
 * E01-F04 + F09).
 *
 * Renders a single firm-template row in the Methodology library screen,
 * mirroring the `.tpl-row` pattern in
 * `design_handoff_caliber_v2/prototype/screens-firm.js → firmLibrary()`:
 * a left-side kind icon, the template name + status chip + version
 * selector + meta line in the middle, and the version-aware actions
 * (Preview, Edit widget defaults) on the right.
 *
 * Version history is loaded lazily — the selector shows the most-recent
 * version label first and reveals older versions through a native
 * `<select>` so the row stays compact. Selecting an older version is a
 * read-only navigation hint (T-0114 widget-defaults modal will consume
 * `data-open-widgets` so the framework-version it should edit is the
 * currently-selected one).
 *
 * Retire / restore is a stub button in v1 — the T-0025 backend exposes
 * POST /api/v1/templates/:id/retire but the curation-write flows ship in
 * a later wave; we surface the affordance per the prototype but keep it
 * disabled with a tooltip.
 */

const KIND_ICON: Record<LibraryKind, typeof BookOpen> = {
  Framework: Grid3x3,
  'Framework overlay': Layers,
  'Register schema': List,
  'Widget defaults': Database,
};

interface FrameworkCardProps {
  template: TemplateSummary;
  kind: LibraryKind;
  /** Currently-selected version id; null when no version is selected (or while loading). */
  selectedVersionId: string | null;
  onSelectVersion: (versionId: string | null) => void;
  /** Fired when the consultant clicks the "Edit widget defaults" affordance. */
  onEditWidgets: (frameworkVersionId: string) => void;
}

export function FrameworkCard({
  template,
  kind,
  selectedVersionId,
  onSelectVersion,
  onEditWidgets,
}: FrameworkCardProps) {
  const versionsQuery = useTemplateVersionsQuery(template.id);
  const KindIcon = KIND_ICON[kind];

  const versions = versionsQuery.data?.versions ?? [];
  const latest = versions.length > 0 ? versions[versions.length - 1]! : null;
  const effectiveVersion: TemplateVersionSummary | null =
    versions.find((v) => v.id === selectedVersionId) ?? latest;

  const isRetired = template.status === 'retired';
  const isEngagementDerived = template.origin === 'engagement_derived';

  return (
    <div
      className={cn(
        'flex flex-wrap items-start gap-4 rounded-card border border-ink-100 bg-paper-0 p-4',
        isRetired && 'opacity-70',
      )}
      data-template-id={template.id}
    >
      <div
        aria-hidden="true"
        className="flex h-9 w-9 flex-none items-center justify-center rounded-card bg-paper-100 text-ink-700"
      >
        <KindIcon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <h3 className="text-body font-semibold text-ink-900">{template.name}</h3>
          <span className="font-mono text-meta text-ink-500">
            {effectiveVersion
              ? `v${effectiveVersion.version}`
              : versionsQuery.isLoading
                ? '…'
                : 'no versions'}
          </span>
          <StatusChip status={template.status} />
          {isEngagementDerived ? (
            <span
              className="inline-flex h-[22px] items-center rounded-full bg-ai-surface px-2.5 text-meta font-medium text-ai-ink"
              title="Promoted back from an engagement"
            >
              Engagement-derived
            </span>
          ) : null}
        </div>
        <div className="mt-1 text-meta text-ink-500">
          {versions.length > 0
            ? `${versions.length} version${versions.length === 1 ? '' : 's'} on record`
            : versionsQuery.isLoading
              ? null
              : 'No published versions yet.'}
          {effectiveVersion ? (
            <>
              {' · '}Latest published {formatDate(effectiveVersion.published_at)}
            </>
          ) : null}
        </div>

        {versionsQuery.error ? (
          <div className="mt-2">
            <ErrorMessage error={versionsQuery.error} />
          </div>
        ) : null}

        {versions.length > 1 ? (
          <label className="mt-3 inline-flex items-center gap-2 text-meta text-ink-500">
            <span>Version</span>
            <select
              value={effectiveVersion?.id ?? ''}
              onChange={(e) => onSelectVersion(e.target.value || null)}
              className="h-8 rounded border border-ink-200 bg-paper-0 px-2 font-mono text-meta text-ink-700 focus:outline-none focus:ring-2 focus:ring-accent-amber-600"
            >
              {[...versions].reverse().map((v) => (
                <option key={v.id} value={v.id}>
                  v{v.version} · {formatDate(v.published_at)}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>

      <div className="flex flex-none items-center gap-2 self-start">
        {versionsQuery.isLoading ? (
          <Spinner />
        ) : (
          <>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={!effectiveVersion}
              title="Preview validation rules + register-schema shape"
            >
              <Eye className="h-3.5 w-3.5" aria-hidden="true" />
              Preview
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!effectiveVersion || isRetired}
              onClick={() => {
                if (effectiveVersion) onEditWidgets(effectiveVersion.id);
              }}
              title="Edit widget defaults for this framework version"
              data-open-widgets={effectiveVersion?.id ?? undefined}
            >
              <Edit3 className="h-3.5 w-3.5" aria-hidden="true" />
              Edit
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled
              title={isRetired ? 'Already retired' : 'Retiring a framework is not available yet.'}
            >
              <Archive className="h-3.5 w-3.5" aria-hidden="true" />
              {isRetired ? 'Retired' : 'Retire'}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function StatusChip({ status }: { status: TemplateSummary['status'] }) {
  if (status === 'retired') {
    return (
      <span className="inline-flex h-[22px] items-center rounded-full bg-ink-100 px-2.5 text-meta font-medium text-ink-500">
        Retired
      </span>
    );
  }
  return (
    <span className="inline-flex h-[22px] items-center rounded-full bg-posture-healthy-tint px-2.5 text-meta font-medium text-posture-healthy">
      Active
    </span>
  );
}
